import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackCta } from "@/lib/analytics";
import { smoothScrollTo } from "@/hooks/useLenis";

const VARIANTS = {
  primary:
    "bg-etap-primary text-white hover:bg-etap-deep shadow-etap-md hover:shadow-etap-glow",
  deep: "bg-etap-deep text-white hover:bg-etap-deep-600 shadow-etap-md",
  mint: "bg-etap-mint-200 text-etap-deep-700 hover:bg-etap-mint-300 shadow-etap-sm",
  outline:
    "border border-etap-primary/40 text-etap-deep-600 bg-white hover:border-etap-primary hover:bg-etap-primary-50",
  ghost: "text-etap-deep-600 hover:text-etap-primary hover:bg-etap-primary-50",
  onDeepOutline:
    "border border-white/30 text-white hover:bg-white/10 hover:border-white/60",
};

const SIZES = {
  md: "h-11 px-6 text-sm",
  lg: "h-13 px-8 text-base py-3.5",
};

export default function Cta({
  children,
  to,
  href,
  onClick,
  variant = "primary",
  size = "md",
  location,
  withArrow = false,
  className = "",
  testId,
  ...rest
}) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full font-display font-semibold tracking-tight transition-[background-color,box-shadow,border-color,color,transform] duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-etap-primary focus-visible:ring-offset-2";
  const cls = `${base} ${VARIANTS[variant] || VARIANTS.primary} ${SIZES[size] || SIZES.md} ${className}`;

  const arrow = withArrow ? (
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
  ) : null;

  const handleClick = (e) => {
    if (location) trackCta(location);
    if (typeof to === "string" && to.startsWith("#")) {
      e.preventDefault();
      smoothScrollTo(to);
    }
    if (onClick) onClick(e);
  };

  // Hash anchors (in-page scroll)
  if (typeof to === "string" && to.startsWith("#")) {
    return (
      <a href={to} onClick={handleClick} className={cls} data-testid={testId} {...rest}>
        {children}
        {arrow}
      </a>
    );
  }

  // External link
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={cls}
        data-testid={testId}
        {...rest}
      >
        {children}
        {arrow}
      </a>
    );
  }

  // Button (no destination)
  if (!to) {
    return (
      <button type="button" onClick={handleClick} className={cls} data-testid={testId} {...rest}>
        {children}
        {arrow}
      </button>
    );
  }

  // Internal route
  return (
    <Link to={to} onClick={handleClick} className={cls} data-testid={testId} {...rest}>
      {children}
      {arrow}
    </Link>
  );
}
